import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { Identificable } from 'shared/types/commonView';
import { isRichTextEmpty } from 'shared/utils/richText';

interface ProtocolAnswerBase extends Identificable {
  checklistAnswerId: string;
  type: AnswerType;
  isApplicable: boolean;
}

export interface ComplianceProtocolAnswer extends ProtocolAnswerBase {
  type: AnswerType.Compliance;
  selectedOption?: ComplianceAnswerOption;
}

export interface NumericProtocolAnswer extends ProtocolAnswerBase {
  type: AnswerType.Numeric;
  numericValue?: number;
}

export interface TextProtocolAnswer extends ProtocolAnswerBase {
  type: AnswerType.Text;
  textValue?: string;
}

export interface OptionSelectionProtocolAnswer extends ProtocolAnswerBase {
  type: AnswerType.OptionSelection;
  selectedOptionIds?: string[];
}

export type ProtocolAnswer =
  | ComplianceProtocolAnswer
  | NumericProtocolAnswer
  | TextProtocolAnswer
  | OptionSelectionProtocolAnswer;

export function isComplianceProtocolAnswer(
  answer: ProtocolAnswer
): answer is ComplianceProtocolAnswer {
  return answer.type === AnswerType.Compliance;
}

export function isNumericProtocolAnswer(
  answer: ProtocolAnswer
): answer is NumericProtocolAnswer {
  return answer.type === AnswerType.Numeric;
}

export function isTextProtocolAnswer(
  answer: ProtocolAnswer
): answer is TextProtocolAnswer {
  return answer.type === AnswerType.Text;
}

export function isOptionSelectionProtocolAnswer(
  answer: ProtocolAnswer
): answer is OptionSelectionProtocolAnswer {
  return answer.type === AnswerType.OptionSelection;
}

export function hasAnswerValue(answer: ProtocolAnswer): boolean {
  if (answer.isApplicable === false) return false;

  switch (answer.type) {
    case AnswerType.Compliance:
      return (
        answer.selectedOption !== undefined &&
        answer.selectedOption !== ComplianceAnswerOption.unset
      );
    case AnswerType.Numeric:
      return answer.numericValue !== undefined;
    case AnswerType.Text:
      return (
        answer.textValue !== undefined && !isRichTextEmpty(answer.textValue)
      );
    case AnswerType.OptionSelection:
      return (
        answer.selectedOptionIds !== undefined &&
        answer.selectedOptionIds.length > 0
      );
  }
}
